import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';

const Index = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#F0F4FF] to-[#E6F0FF] text-gray-800 overflow-hidden">
      {/* Geometric background elements */}
      <div className="absolute inset-0 overflow-hidden opacity-20 pointer-events-none">
        <div className="absolute top-0 left-0 w-96 h-96 bg-[#7000FF] rounded-full filter blur-3xl -translate-x-1/2 -translate-y-1/2"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-[#00FFC6] rounded-full filter blur-3xl translate-x-1/2 translate-y-1/2"></div>
        <div className="grid grid-cols-12 grid-rows-12 h-screen w-screen">
          {Array.from({ length: 24 }).map((_, i) => (
            <div key={i} className="border-[0.5px] border-gray-200"></div>
          ))}
        </div>
      </div>

      {/* Navigation */}
      <nav className="relative z-10 px-6 py-6 flex justify-between items-center">
        <motion.div 
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5 }}
          className="flex items-center"
        >
          <span className="text-2xl font-bold tracking-tight bg-clip-text text-transparent bg-gradient-to-r from-[#7000FF] to-[#00FFC6]">
            ABMR Holdings
          </span>
          <span className="ml-2 text-sm uppercase tracking-widest text-gray-600">Family Office</span>
        </motion.div>
        
        <motion.div 
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="hidden md:flex space-x-8 text-sm font-medium"
        >
          <a href="#about" className="hover:text-[#00FFC6] transition-colors">About</a>
          <a href="#investments" className="hover:text-[#00FFC6] transition-colors">Investments</a>
          <a href="#ventures" className="hover:text-[#00FFC6] transition-colors">Ventures</a>
          <a href="#contact" className="hover:text-[#00FFC6] transition-colors">Contact</a>
        </motion.div>
        
        <motion.button
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
          onClick={() => navigate('/login')}
          className="px-5 py-2 rounded-full bg-gradient-to-r from-[#7000FF] to-[#00FFC6] text-white font-medium text-sm hover:shadow-lg hover:shadow-[#7000FF]/20 transition-all"
        >
          Login
        </motion.button>
      </nav>

      {/* Hero Section */}
      <section className="relative z-10 px-6 md:px-12 pt-20 pb-32">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="max-w-3xl"
          >
            <div>
              <h1 className="text-5xl md:text-7xl font-bold leading-tight text-gray-800">
                Building the <span className="bg-clip-text text-transparent bg-gradient-to-r from-[#7000FF] to-[#00FFC6]">future</span> of wealth
              </h1>
              <p className="mt-6 text-lg text-gray-600 leading-relaxed">
                A forward-thinking family office focused on strategic investments and innovative ventures that shape tomorrow's landscape.
              </p>
            </div>

          </motion.div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="relative z-10 px-6 md:px-12 py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.7 }}
            viewport={{ once: true }}
            className="grid md:grid-cols-2 gap-12 items-center"
          >
            <div>
              <h2 className="text-3xl md:text-4xl font-bold leading-tight text-gray-800">
                About <span className="bg-clip-text text-transparent bg-gradient-to-r from-[#7000FF] to-[#00FFC6]">ABMR Holdings</span>
              </h2>
              <div className="mt-6 space-y-4 text-gray-600">
                <p>
                  Founded in 2022, ABMR Holdings Family Office manages the assets and investments of the Azhar Iqubal's family, with a focus on technology, AI and disruptive innovation.
                </p>
                <p>
                  Our approach combines traditional wealth preservation strategies with forward-thinking investment in emerging technologies and markets.
                </p>
                <p>
                  Led by Azhar Iqubal, tech entrepreneur and investor, our team brings decades of experience across technology, media and AI.
                </p>
              </div>
              <div className="mt-8 grid grid-cols-2 gap-4">
                <div className="p-4 rounded-lg bg-white shadow-sm">
                  <p className="text-[#00FFC6] font-bold text-2xl">2+</p>
                  <p className="text-gray-500 text-sm">Years</p>
                </div>
                <div className="p-4 rounded-lg bg-white shadow-sm">
                  <p className="text-[#00FFC6] font-bold text-2xl">1</p>
                  <p className="text-gray-500 text-sm">Team Member</p>
                </div>
              </div>
            </div>
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-r from-[#7000FF]/20 to-[#00FFC6]/20 rounded-2xl transform rotate-3"></div>
              <img 
                src="https://images.lifestyleasia.com/wp-content/uploads/sites/7/2023/10/17194226/Azhar-Iqubal.jpg" 
                alt="Azhar Iqubal" 
                className="relative z-10 rounded-2xl object-cover w-full h-[400px]"
              />
            </div>
          </motion.div>
        </div>
      </section>

      {/* Investment Portfolio Section */}
      <section id="investments" className="relative z-10 px-6 md:px-12 py-24">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-800">
              Our Investment <span className="bg-clip-text text-transparent bg-gradient-to-r from-[#7000FF] to-[#00FFC6]">Portfolio</span>
            </h2>
            <p className="mt-4 text-gray-600 max-w-2xl mx-auto">
              A diverse collection of strategic investments across multiple sectors, focusing on innovation and long-term growth potential.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                name: "QuickReply.ai",
                sector: "Customer Engagement",
                year: 2021,
                description: "QuickReply.ai finds the best time and opportunities to nudge your contacts with flows like WhatsApp broadcasts, multi-step abandoned cart recovery, convert COD orders to prepaid, and many more.",
                image: "https://images.unsplash.com/photo-1611162617213-7d7a39e9b1d7?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2232&q=80"
              },
              {
                name: "Riyalto",
                sector: "Jewelry Marketplace",
                year: 2020,
                description: "We enable seamless trade between Jewelry retailers, manufacturers, and designers; provide a competitive edge to Independent Jewelry retailers worldwide.",
                image: "https://images.unsplash.com/photo-1515562141207-7a88fb7ce338?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80"
              },
              {
                name: "Reslink",
                sector: "Renewable Energy Storage",
                year: 2023,
                description: "At Reslink we are solving renewable energy gaps in the grid & intermittent nature of RE by building affordable BESS, designing scalable energy storage systems that make renewable energy more reliable and accessible.",
                image: "https://images.unsplash.com/photo-1473341304170-971dccb5ac1e?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80"
              },
            ].map((investment, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="group bg-white shadow-sm rounded-xl overflow-hidden hover:shadow-md transition-all duration-300"
              >
                <div className="h-48 overflow-hidden">
                  <img 
                    src={investment.image} 
                    alt={investment.name} 
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-6">
                  <div className="flex justify-between items-start mb-3">
                    <h3 className="text-xl font-bold text-gray-800">{investment.name}</h3>
                    <span className="text-xs bg-[#7000FF]/20 text-[#7000FF] px-2 py-1 rounded-full">{investment.year}</span>
                  </div>
                  <p className="text-sm text-[#00FFC6] mb-3">{investment.sector}</p>
                  <p className="text-gray-600 text-sm">{investment.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
          
        </div>
      </section>

      {/* Ventures Section */}
      <section id="ventures" className="relative z-10 px-6 md:px-12 py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-800">
              Our <span className="bg-clip-text text-transparent bg-gradient-to-r from-[#7000FF] to-[#00FFC6]">Ventures</span>
            </h2>
            <p className="mt-4 text-gray-600 max-w-2xl mx-auto">
              Exploring innovative ideas and emerging opportunities that have the potential to disrupt industries and create significant value.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-1 gap-8">
            {[
              {
                name: "Fenado AI",
                description: "Fenado AI aims to replace the tech teams of companies with AI."
              },
            ].map((venture, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: index % 2 === 0 ? -20 : 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5 }}
                viewport={{ once: true }}
                className="bg-gradient-to-br from-[#7000FF]/10 to-[#00FFC6]/10 p-[1px] rounded-xl"
              >
                <div className="bg-white shadow-sm rounded-xl p-8 h-full">
                  <div className="mb-4">
                    <h3 className="text-2xl font-bold text-gray-800">{venture.name}</h3>
                  </div>
                  <p className="text-gray-600">{venture.description}</p>
                  <div className="mt-6 flex justify-end">
                    <div className="flex -space-x-2">
                      {[1, 2, 3].map((_, i) => (
                        <div key={i} className="w-8 h-8 rounded-full bg-[#7000FF]/30 border border-[#7000FF]/50"></div>
                      ))}
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="relative z-10 px-6 md:px-12 py-24">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl md:text-4xl font-bold text-gray-800">
                Get in <span className="bg-clip-text text-transparent bg-gradient-to-r from-[#7000FF] to-[#00FFC6]">Touch</span>
              </h2>
              <p className="mt-4 text-gray-600">
                Interested in learning more about our investment approach or discussing potential opportunities? Our team is ready to connect.
              </p>
              <div className="mt-8 space-y-6">
                <div className="flex items-center space-x-4">
                  <div className="w-10 h-10 rounded-full bg-[#7000FF]/20 flex items-center justify-center">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-[#00FFC6]" viewBox="0 0 20 20" fill="currentColor">
                      <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                      <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
                    </svg>
                  </div>
                  <div>
                    <p className="text-gray-500 text-sm">Email</p>
                    <p className="text-gray-800">contact@abmrholdings.com</p>
                  </div>
                </div>
                <div className="flex items-center space-x-4">
                  <div className="w-10 h-10 rounded-full bg-[#7000FF]/20 flex items-center justify-center">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-[#00FFC6]" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div>
                    <p className="text-gray-500 text-sm">Location</p>
                    <p className="text-gray-800">336 Smith Street, New Bridge Centre, Singapore</p>
                  </div>
                </div>
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.2 }}
              viewport={{ once: true }}
              className="bg-white shadow-sm rounded-xl p-6"
            >
              <form className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-gray-600 text-sm mb-2">Name</label>
                    <input type="text" className="w-full bg-gray-50 border border-gray-200 rounded-lg px-4 py-3 text-gray-800 focus:outline-none focus:border-[#00FFC6]" />
                  </div>
                  <div>
                    <label className="block text-gray-600 text-sm mb-2">Email</label>
                    <input type="email" className="w-full bg-gray-50 border border-gray-200 rounded-lg px-4 py-3 text-gray-800 focus:outline-none focus:border-[#00FFC6]" />
                  </div>
                </div>
                <div>
                  <label className="block text-gray-600 text-sm mb-2">Subject</label>
                  <input type="text" className="w-full bg-gray-50 border border-gray-200 rounded-lg px-4 py-3 text-gray-800 focus:outline-none focus:border-[#00FFC6]" />
                </div>
                <div>
                  <label className="block text-gray-600 text-sm mb-2">Message</label>
                  <textarea rows={4} className="w-full bg-gray-50 border border-gray-200 rounded-lg px-4 py-3 text-gray-800 focus:outline-none focus:border-[#00FFC6]"></textarea>
                </div>
                <button className="w-full py-3 rounded-lg bg-gradient-to-r from-[#7000FF] to-[#00FFC6] text-white font-medium hover:shadow-lg hover:shadow-[#7000FF]/20 transition-all">
                  Send Message
                </button>
              </form>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="relative z-10 px-6 md:px-12 py-12 bg-gray-100 border-t border-gray-200">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-3 gap-8">
            <div>
              <div className="flex items-center">
                <span className="text-2xl font-bold tracking-tight bg-clip-text text-transparent bg-gradient-to-r from-[#7000FF] to-[#00FFC6]">
                  ABMR Holdings
                </span>
                <span className="ml-2 text-sm uppercase tracking-widest text-gray-600">Family Office</span>
              </div>
              <p className="mt-4 text-gray-500 text-sm">
                Building wealth through strategic investments and innovative ventures.
              </p>
            </div>
            
            <div>
              <h3 className="text-gray-800 font-medium mb-4">Quick Links</h3>
              <ul className="space-y-2 text-gray-600 text-sm">
                <li><a href="#about" className="hover:text-[#00FFC6] transition-colors">About Us</a></li>
                <li><a href="#investments" className="hover:text-[#00FFC6] transition-colors">Investments</a></li>
                <li><a href="#ventures" className="hover:text-[#00FFC6] transition-colors">Ventures</a></li>
                <li><a href="#contact" className="hover:text-[#00FFC6] transition-colors">Contact</a></li>
              </ul>
            </div>
            
            <div>
              <h3 className="text-gray-800 font-medium mb-4">Legal</h3>
              <ul className="space-y-2 text-gray-600 text-sm">
                <li><button onClick={() =>navigate('/privacy-policy')} className="hover:text-[#00FFC6] transition-colors">Privacy Policy</button></li>
                <li><button onClick={() => navigate('/terms-of-service')} className="hover:text-[#00FFC6] transition-colors">Terms of Service</button></li>
                <li><button onClick={() => navigate('/disclaimer')} className="hover:text-[#00FFC6] transition-colors">Disclaimer</button></li>
              </ul>
            </div>
          </div>
          
          <div className="mt-12 pt-8 border-t border-gray-200 text-center text-gray-500 text-sm">
            <p>© {new Date().getFullYear()} ABMR Holdings Family Office. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Index;
